import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useCreators } from "@/context/CreatorsContext";
import { Users, Link2, Eye, Plus, ExternalLink, UserPlus, Clock, Star, Globe, Shield, Mail, Crown, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";

const AdminDashboard = () => {
  const { creators, getActiveCreators, getFeaturedCreators, fetchAllCreators, isLoading } = useCreators();
  const [vipRequests, setVipRequests] = useState([]);
  const [loadingVip, setLoadingVip] = useState(true);
  
  // Fetch all creators on mount
  useEffect(() => {
    fetchAllCreators();
  }, [fetchAllCreators]);

  // Fetch VIP requests
  useEffect(() => {
    const fetchVipRequests = async () => {
      try {
        const response = await fetch("/api/admin/telegram-vip-requests", {
          headers: {
            "Authorization": `Basic ${btoa('admin:business')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setVipRequests(data);
        }
      } catch (error) {
        console.error("Error fetching VIP requests:", error);
      } finally {
        setLoadingVip(false);
      }
    };
    fetchVipRequests();
  }, []);

  // Filter approved memberships
  const activeMemberships = vipRequests.filter(r => r.status === "approved");
  const pendingRequests = vipRequests.filter(r => r.status === "pending");
  
  const activeCreators = getActiveCreators();
  const featuredCreators = getFeaturedCreators();
  const totalLinks = creators.reduce((acc, c) => acc + (c.links?.length || 0), 0);
  
  // Separate by registration type
  const webRegistrations = creators.filter(c => c.registration_type === "web");
  const adminCreated = creators.filter(c => c.registration_type !== "web");
  
  // Sort creators by creation date (most recent first)
  const sortByDate = (list) => [...list].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0);
    const dateB = new Date(b.createdAt || 0);
    return dateB - dateA;
  });

  // Format relative time
  const formatTimeAgo = (dateString) => {
    if (!dateString) return "Fecha desconocida";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Ahora mismo";
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const stats = [
    {
      label: "Total Usuarios",
      value: creators.length,
      icon: Users,
      gradient: "from-slate-500 to-blue-500"
    },
    {
      label: "Registros Web",
      value: webRegistrations.length,
      icon: Globe,
      gradient: "from-green-500 to-emerald-500",
      highlight: true
    },
    {
      label: "Creados por Admin",
      value: adminCreated.length,
      icon: Shield,
      gradient: "from-purple-500 to-indigo-500"
    },
    {
      label: "Destacados",
      value: featuredCreators.length,
      icon: Star,
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  const renderUserList = (users, title, icon, iconGradient, showEmail = false) => (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3">
          <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br ${iconGradient} flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
          <div>
            <h2 className="text-sm md:text-lg font-semibold text-white">{title}</h2>
            <p className="text-xs text-white/50">{users.length} usuarios</p>
          </div>
        </div>
        <Link to="/admin/creadoras">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 text-xs md:text-sm px-2 md:px-3">
            Ver todos
          </Button>
        </Link>
      </div>

      {users.length > 0 ? (
        <div className="space-y-2 md:space-y-3 max-h-[400px] overflow-y-auto">
          {sortByDate(users).slice(0, 10).map((creator) => (
            <div key={creator.id} className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
              {creator.avatar ? (
                <img
                  src={creator.avatar}
                  alt={creator.name}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover ring-2 ring-white/10 flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-slate-600 to-blue-600 flex items-center justify-center ring-2 ring-white/10 flex-shrink-0">
                  <span className="text-white font-bold text-sm md:text-lg">
                    {creator.name?.charAt(0) || creator.email?.charAt(0) || '?'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-white text-sm md:text-base truncate">{creator.name || 'Sin nombre'}</h3>
                <p className="text-xs md:text-sm text-white/50 truncate">@{creator.username || 'sin-username'}</p>
                {showEmail && creator.email && (
                  <p className="text-xs text-white/40 flex items-center gap-1 mt-1 truncate">
                    <Mail className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{creator.email}</span>
                  </p>
                )}
              </div>
              <div className="hidden sm:flex items-center gap-2 flex-wrap justify-end">
                {creator.featured && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                    <Star className="w-3 h-3" />
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  creator.active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-white/10 text-white/50'
                }`}>
                  {creator.active ? 'Activo' : 'Inactivo'}
                </span>
                <div className="flex items-center gap-1 text-white/40 text-xs">
                  <Clock className="w-3 h-3" />
                  <span>{formatTimeAgo(creator.createdAt)}</span>
                </div>
                <a
                  href={`/${creator.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
              {/* Mobile: simplified actions */}
              <div className="flex sm:hidden items-center gap-1">
                {creator.featured && <Star className="w-3 h-3 text-yellow-400" />}
                <a
                  href={`/${creator.username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-white/40"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-white/50">No hay usuarios en esta categoría</p>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout 
      title="Dashboard" 
      subtitle="Resumen general de JGlinks"
      actions={
        <Link to="/admin/creadoras/nueva">
          <Button className="rounded-xl bg-gradient-to-r from-slate-600 to-blue-600 hover:from-slate-700 hover:to-blue-700 border-0">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Creadora
          </Button>
        </Link>
      }
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
        {stats.map((stat) => (
          <div key={stat.label} className={`bg-white/5 rounded-xl md:rounded-2xl border ${stat.highlight ? 'border-green-500/30' : 'border-white/10'} p-3 md:p-5`}>
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center flex-shrink-0`}>
                <stat.icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <div>
                <p className="text-lg md:text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-[10px] md:text-xs text-white/50 leading-tight">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Web Registrations - Users to charge */}
        {renderUserList(
          webRegistrations, 
          "Registros Web (Cobrar)", 
          <Globe className="w-5 h-5 text-white" />,
          "from-green-500 to-emerald-600",
          true
        )}
        
        {/* Admin Created */}
        {renderUserList(
          adminCreated, 
          "Creados por Admin", 
          <Shield className="w-5 h-5 text-white" />,
          "from-purple-500 to-indigo-600",
          false
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
